import { _converse, api } from '@converse/headless';
import BaseModal from 'plugins/modal/modal.js';
import { html } from 'lit';
import { __ } from 'i18n';
import { stopSelecting } from 'shared/chat/selection.js';

import './styles/forward-modal.scss';

export default class ForwardModal extends BaseModal {
    static properties = {
        selectedTargets: { type: Array },
        searchQuery: { type: String },
        _submitting: { type: Boolean },
    };

    constructor(options) {
        super(options);
        this.model = options?.model || null;
        // Chatbox to exclude from the target list. The legacy single-message call
        // site passes the message as `model`, so derive its chatbox from there.
        this.chatbox = options?.chatbox || options?.model?.collection?.chatbox || options?.model || null;
        // Multiple selected messages (Telegram-style select mode) take priority
        // over the legacy single `forwardText` string.
        this.messages = options?.messages || null;
        this.forwardText = options?.forwardText || '';
        this.selectedTargets = [];
        this.searchQuery = '';
        this._submitting = false;
    }

    getModalTitle() {
        return this.messages?.length > 1 ? __('Переслати %1$s повідомлень', this.messages.length) : __('Переслати');
    }

    // Час останнього реального повідомлення в чаті (як у списку "Усі чати"),
    // щоб недавні контакти/групи піднімалися вгору списку пересилання.
    getLastMsgTime(jid) {
        const chatbox = _converse.chatboxes.get(jid);
        if (!chatbox || !chatbox.messages) return 0;

        const messages = chatbox.messages.models || [];
        for (let i = messages.length - 1; i >= 0; i--) {
            const m = messages[i];
            const hasBody = !!(m.get('body') || m.get('plaintext'));
            if (m.get('type') !== 'info' && hasBody) {
                const time = m.get('time');
                return time ? new Date(time).getTime() : 0;
            }
        }
        return 0;
    }

    // Правильное получение списка чатов без дублей
    get availableTargets() {
        const query = this.searchQuery.toLowerCase();
        const unique = new Map();

        // 1. Добавляем активные чаты (и личные, и группы)
        (_converse.chatboxes?.models || []).forEach((c) => {
            unique.set(c.get('jid'), c);
        });

        // 2. Добавляем закладки (группы, в которых мы состоим)
        (_converse.bookmarks?.models || []).forEach((b) => {
            if (!unique.has(b.get('jid'))) {
                unique.set(b.get('jid'), b);
            }
        });

        return Array.from(unique.values())
            .filter((i) => {
                const jid = i.get('jid');
                if (!jid || jid === this.chatbox?.get('jid')) return false;
                const name = (i.getDisplayName() || jid).toLowerCase();
                return name.includes(query);
            })
            .sort((a, b) => {
                // Недавні (за часом останнього повідомлення) — вгорі, як в інших месенджерах.
                const timeA = this.getLastMsgTime(a.get('jid'));
                const timeB = this.getLastMsgTime(b.get('jid'));
                if (timeA !== timeB) return timeB - timeA;

                // Без листування — за алфавітом, як фолбек.
                const nameA = (a.getDisplayName() || '').toLowerCase();
                const nameB = (b.getDisplayName() || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
    }

    toggleTarget(jid) {
        this.selectedTargets = this.selectedTargets.includes(jid)
            ? this.selectedTargets.filter((j) => j !== jid)
            : [...this.selectedTargets, jid];
    }

    async forwardMessages(ev) {
        ev.preventDefault();
        // Multi-select forward (Telegram-style): send each selected message, in
        // chronological order, as its own stanza. Falls back to the legacy
        // single-text path used by the per-message "Переслати" action.
        const textsToSend = this.messages?.length
            ? this.messages.map((m) => m.getMessageText()).filter(Boolean)
            : this.forwardText
              ? [this.forwardText]
              : [];

        if (this._submitting || this.selectedTargets.length === 0 || textsToSend.length === 0) return;

        this._submitting = true;
        console.log(`[Forward] Старт. Targets: ${this.selectedTargets.length}, messages: ${textsToSend.length}`);

        try {
            for (const jid of this.selectedTargets) {
                // 1. Попытка получить через API
                let chat = await api.chats.get(jid, {}, true);

                // 2. Если API вернуло пустоту или нерабочий чат, ищем в коллекции напрямую
                if (!chat) {
                    chat = _converse.chatboxes.findWhere({ jid: jid });
                }

                // 3. Если чат все еще не найден, но это MUC, пробуем создать комнату через API комнат
                if (!chat && jid.includes('@conference')) {
                    console.log(`[Forward] Попытка принудительного открытия комнаты: ${jid}`);
                    chat = await api.rooms.open(jid);
                }

                if (!chat) {
                    console.error(`[Forward] Не удалось инициализировать чат для JID: ${jid}`);
                    continue;
                }

                console.log(`[Forward] Чат найден, тип: ${chat.get('type')}. Отправка...`);

                // 4. sendMessage() silently EDITS whichever message is flagged
                // 'correcting' instead of creating a new one (see
                // model-with-messages.js). If the target chat has a stray
                // correcting message (e.g. an abandoned edit), our first forwarded
                // text would overwrite it in place instead of appearing as a new
                // message — which looks like "only the last message got sent"
                // when forwarding more than one. Clear it defensively first.
                const correcting = chat.messages?.findWhere?.('correcting');
                if (correcting) correcting.save('correcting', false);

                // 5. Используем sendMessage, но оборачиваем в try/catch для конкретного чата
                for (const textToSend of textsToSend) {
                    try {
                        await chat.sendMessage({ 'body': textToSend });
                        console.log(`[Forward] Успешно отправлено в ${jid}`);
                    } catch (innerErr) {
                        console.error(`[Forward] Ошибка отправки в ${jid}:`, innerErr);
                    }
                }
            }

            if (this.messages?.length && this.chatbox) {
                stopSelecting(this.chatbox);
            }

            // A toast rather than the alert modal: it's a transient confirmation,
            // not something the user needs to explicitly dismiss, and it sidesteps
            // this app's `.modal-content { color: #333 !important }` override
            // (in 3rdparty/main.css) that made the modal's own text unreadable.
            api.toast.show('forward-success', {
                type: 'success',
                body: __('Повідомлення переслано'),
                duration: 3000,
            });
            this.modal.hide();
        } catch (e) {
            console.error('[Forward] Критическая ошибка:', e);
            api.toast.show('forward-error', { type: 'danger', body: __('Не вдалося переслати повідомлення') });
        } finally {
            this._submitting = false;
        }
    }

    // async forwardMessages(ev) {
    //     ev.preventDefault();
    //     const textToSend = this.forwardText;
    //     if (this._submitting || this.selectedTargets.length === 0 || !textToSend) return;

    //     this._submitting = true;

    //     try {
    //         for (const jid of this.selectedTargets) {
    //             const chat = await api.chats.get(jid, {}, true);
    //             if (chat) {
    //                 // ВАЖНО: передаем объект, а не строку
    //                 await chat.sendMessage({ 'body': textToSend });
    //                 console.log(`Sent to ${jid}: ${textToSend}`);
    //             }
    //         }
    //         api.alert('success', __('Success'), __('Message forwarded'));
    //         this.modal.hide();
    //     } catch (e) {
    //         console.error('Forwarding Error:', e);
    //         api.alert('error', __('Error'), __('Failed to forward message'));
    //     } finally {
    //         this._submitting = false;
    //     }
    // }
    renderModal() {
        return html`<div class="forward-modal-content d-flex flex-column h-100 p-3"><div class="search-wrapper position-relative mb-3"><input type="search" class="form-control rounded-pill bg-dark border-0 text-white ps-5" placeholder="${__('Пошук...')}" @input="${(ev) => (this.searchQuery = ev.target.value)}" .value="${this.searchQuery}"></div><div class="contacts-list flex-grow-1 overflow-auto mb-3" style="max-height:400px">${this.availableTargets.map((target) => {
                        const jid = target.get('jid');
                        const isSelected = this.selectedTargets.includes(jid);
                        const name = target.getDisplayName();
                        const indicatorStyle = `width: 24px; height: 24px; border: 1px solid ${isSelected ? '#4a76eb' : '#666'}; background-color: ${isSelected ? '#4a76eb' : 'transparent'};`;

                        return html`<div class="contact-item d-flex align-items-center justify-content-between p-3 border-bottom border-secondary border-opacity-10" @click="${() => this.toggleTarget(jid)}" style="cursor:pointer"><div class="d-flex align-items-center"><div class="text-white">${name}</div></div><div class="selection-indicator"><div class="rounded-circle d-flex align-items-center justify-content-center" style="${indicatorStyle}">${isSelected
                                            ? html`<i class="fa fa-check text-white" style="font-size:10px"></i>`
                                            : ''}</div></div></div>`;
                    })}</div><button class="btn btn-primary w-100 rounded-pill py-2 fw-bold" style="background-color:#4a76eb" ?disabled="${this._submitting || this.selectedTargets.length === 0}" @click="${this.forwardMessages}">${this._submitting ? __('Зачекайте...') : __('Переслати')}</button></div>`;
    }

    // renderModal() {
    //     return html`
    //         <div class="forward-modal-content">
    //             <input
    //                 type="search"
    //                 class="form-control mb-3"
    //                 placeholder="${__('Пошук...')}"
    //                 @input="${(ev) => (this.searchQuery = ev.target.value)}"
    //             />

    //             <div class="list-group mb-3" style="max-height: 400px; overflow-y: auto;">
    //                 ${this.availableTargets.map((target) => {
    //                     const jid = target.get('jid');
    //                     return html`
    //                         <div
    //                             class="list-group-item d-flex align-items-center"
    //                             @click="${() => this.toggleTarget(jid)}"
    //                             style="cursor:pointer"
    //                         >
    //                             <input type="checkbox" class="me-2" .checked="${this.selectedTargets.includes(jid)}" />
    //                             <span>${target.getDisplayName()}</span>
    //                         </div>
    //                     `;
    //                 })}
    //             </div>

    //             <button
    //                 class="btn btn-primary w-100"
    //                 ?disabled="${this._submitting || this.selectedTargets.length === 0}"
    //                 @click="${this.forwardMessages}"
    //             >
    //                 ${__('Переслати')}
    //             </button>
    //         </div>
    //     `;
    // }
}
if (!customElements.get('converse-forward-modal')) {
    customElements.define('converse-forward-modal', ForwardModal);
}
